/**
 * Drawer.tsx — right-slide drawer for drill-in details.
 *
 * Renders nothing when `open=false` so the children can be expensive (full
 * detail tables) without paying for them when closed. Backdrop click +
 * Escape close. Uses the `.drawer` / `.drawer-backdrop` classes from
 * design.css so the look matches the TNSC reference.
 */

import React, { useEffect } from 'react';

import Ico from './Ico';

interface Props {
	open: boolean;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	onClose: () => void;
	width?: number;
	children: React.ReactNode;
}

export default function Drawer({ open, title, subtitle, onClose, width = 560, children }: Props) {
	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') onClose();
		}
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<>
			<div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
			<aside
				className="drawer"
				style={{ width }}
				role="dialog"
				aria-modal="true"
				aria-label={typeof title === 'string' ? title : 'Detail'}
			>
				<header className="drawer-head">
					<div style={{ minWidth: 0 }}>
						<div
							style={{
								fontFamily: 'var(--font-display)',
								fontSize: 16,
								fontWeight: 700,
								color: 'var(--text)',
							}}
						>
							{title}
						</div>
						{subtitle && (
							<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>
								{subtitle}
							</div>
						)}
					</div>
					<button
						type="button"
						className="icon-btn"
						onClick={onClose}
						aria-label="Close"
						title="Close"
					>
						<Ico name="close" size={14} />
					</button>
				</header>
				<div className="drawer-body">{children}</div>
			</aside>
		</>
	);
}
